import Api from "../../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../../core/data/redux/apis/urls";

export const IVF_STATUS_OPTIONS = [
  { key: "", labelKey: "all", label: "All" },
  { key: "N", labelKey: "new", label: "New" },
  { key: "T", labelKey: "to_be_approved", label: "To Be Approved" },
  { key: "P", labelKey: "partially_approved", label: "Partially Approved" },
  { key: "B", labelKey: "to_be_processed", label: "To Be Processed" },
  { key: "A", labelKey: "approved", label: "Approved" },
  { key: "R", labelKey: "rejected", label: "Rejected" },
];

const STATUS_MAP: Record<string, string> = {
  N: "new",
  T: "to_be_approved",
  P: "partially_approved",
  B: "to_be_processed",
  A: "approved",
  R: "rejected",
};

export const REQUEST_TYPE_MAP: Record<string, string> = {
  "62": "Para 6.2",
  "63": "Para 6.3",
  "66": "IVF",
  CO: "CO",
};

export function getLoggedInUser(): any {
  try {
    const raw = localStorage.getItem("user");
    if (!raw || raw === "undefined") return {};
    const parsed = JSON.parse(raw);
    return parsed?.data?.user || parsed?.user || parsed || {};
  } catch {
    return {};
  }
}

export function getLoggedInEmpId(): string {
  const user = getLoggedInUser();
  return String(
    user?.empId ||
      user?.details?.empId ||
      user?.usrid ||
      user?.emp_id ||
      ""
  ).trim();
}

export function padEmpId(empId = getLoggedInEmpId()): string {
  const raw = String(empId || "").trim();
  if (!raw) return "";
  return raw.padStart(8, "0");
}

export function getEmployeeDisplayName(user = getLoggedInUser()): string {
  const details = user?.details || {};
  return (
    [details?.firstname, details?.lsastname || details?.secondname || details?.lastname]
      .filter(Boolean)
      .join(" ")
      .trim() || "-"
  );
}

export function pickField(obj: any, ...keys: string[]): any {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== "") {
      return obj[k];
    }
  }
  return undefined;
}

export function unwrap(res: any): any {
  return res?.data ?? res;
}

export function extractResults(data: any): any[] {
  const root = unwrap(data);
  const inner = root?.data ?? root;
  if (Array.isArray(inner?.d?.results)) return inner.d.results;
  if (Array.isArray(inner?.results)) return inner.results;
  if (Array.isArray(inner?.data)) return inner.data;
  if (Array.isArray(inner)) return inner;
  if (Array.isArray(root?.d?.results)) return root.d.results;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root)) return root;
  return [];
}

export function extractDropdownOptions(data: any): { id: string; label: string }[] {
  return extractResults(data)
    .map((d: any) => ({
      id: String(d.Value ?? d.value ?? ""),
      label: String(d.Ddtext ?? d.DdText ?? d.ddtext ?? d.label ?? ""),
    }))
    .filter((o) => o.id && o.label);
}

export function formatSapDate(value: any): string {
  if (value === undefined || value === null || value === "") return "-";
  const str = String(value).trim();
  const msMatch = str.match(/\/Date\((\d+)\)\//);
  if (msMatch) {
    const date = new Date(parseInt(msMatch[1], 10));
    if (isNaN(date.getTime())) return str;
    return toDmy(date);
  }
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const dmy = str.match(/^(\d{2})[-/.](\d{2})[-/.](\d{4})/);
  if (dmy) return `${dmy[1]}-${dmy[2]}-${dmy[3]}`;
  const date = new Date(str);
  if (!isNaN(date.getTime())) return toDmy(date);
  return str;
}

function toDmy(date: Date): string {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${date.getFullYear()}`;
}

export function formatSapDateTime(value: any): string {
  if (!value) return "-";
  const str = String(value).trim();
  const msMatch = str.match(/\/Date\((\d+)\)\//);
  const date = msMatch ? new Date(parseInt(msMatch[1], 10)) : new Date(str);
  if (isNaN(date.getTime())) return str;
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${dd}-${mm}-${date.getFullYear()} ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
}

export function normalizeDobToDMY(raw: any): string {
  if (raw === undefined || raw === null) return "";
  const s = String(raw).trim();
  if (!s) return "";
  const ms = s.match(/\/Date\((\d+)\)\//);
  if (ms) {
    const d = new Date(parseInt(ms[1], 10));
    return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
  }
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`;
  const dmy = s.match(/^(\d{2})[-/.](\d{2})[-/.](\d{4})/);
  if (dmy) return `${dmy[1]}.${dmy[2]}.${dmy[3]}`;
  return s;
}

export function formDateToApiDate(d: string): string {
  return d ? d.replace(/\./g, "-") : "";
}

export function ymdToDmyDots(ymd: string): string {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  if (!y || !m || !d) return ymd;
  return `${d}.${m}.${y}`;
}

export function dmyDotsToYmd(dmy: string): string {
  if (!dmy) return "";
  const parts = String(dmy).trim().split(/[-./]/);
  if (parts.length < 3) return "";
  const [d, m, y] = parts;
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
}

export function calcAgeFromDMY(dob: string): string {
  if (!dob) return "";
  const parts = String(dob).trim().split(/[-./]/);
  if (parts.length < 3) return "";
  const [d, m, y] = parts.map((p) => parseInt(p, 10));
  if (!d || !m || !y) return "";
  const birth = new Date(y, m - 1, d);
  if (isNaN(birth.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const mDiff = today.getMonth() - birth.getMonth();
  if (mDiff < 0 || (mDiff === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age >= 0 ? String(age) : "";
}

export function getFileExtension(fileName?: string, mimeType?: string): string {
  if (fileName) {
    const ext = fileName.split(".").pop()?.toLowerCase();
    if (ext) return ext === "jpeg" ? "jpg" : ext;
  }
  const mt = String(mimeType || "").toLowerCase();
  if (mt.includes("pdf")) return "pdf";
  if (mt.includes("jpeg") || mt.includes("jpg")) return "jpg";
  if (mt.includes("png")) return "png";
  return "pdf";
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function getStatusLabel(raw: any, t: (key: string) => string): string {
  const code = String(raw ?? "").trim().toUpperCase();
  const found = IVF_STATUS_OPTIONS.find((opt) => opt.key === code);
  if (found) {
    const translated = t(found.labelKey);
    return translated === found.labelKey ? found.label : translated;
  }
  const mapped = STATUS_MAP[code] || String(raw ?? "").trim();
  if (!mapped) return "-";
  const translated = t(mapped);
  return translated === mapped ? mapped : translated;
}

export function getStatusBadgeClass(raw: any): string {
  const code = String(raw ?? "").trim().toUpperCase();
  if (code === "A") return "badge bg-success";
  if (code === "R" || code === "K") return "badge bg-danger";
  if (code === "T" || code === "P") return "badge bg-warning text-dark";
  if (code === "B") return "badge bg-info";
  if (code === "N") return "badge bg-primary";
  return "badge bg-secondary";
}

export function getReferenceNumber(item: any): string {
  return String(pickField(item, "ReferenceNumber", "referenceNumber", "id") ?? "-");
}

export function sortRemarksByTimeStampDesc(list: any[]): any[] {
  return [...list].sort((a, b) => {
    const ta = parseRemarkTime(a?.TimeStamp);
    const tb = parseRemarkTime(b?.TimeStamp);
    return tb - ta;
  });
}

function parseRemarkTime(ts: any): number {
  if (!ts) return 0;
  const str = String(ts);
  const ms = str.match(/\/Date\((\d+)\)\//);
  if (ms) return parseInt(ms[1], 10);
  const d = new Date(str);
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

export function getRemarksList(detail: any): any[] {
  const remarksListRaw = Array.isArray(detail?.ToRemarks?.results)
    ? detail.ToRemarks.results
    : Array.isArray(detail?.ToRemarks)
      ? detail.ToRemarks
      : Array.isArray(detail?.RemarksHistory)
        ? detail.RemarksHistory
        : [];
  return sortRemarksByTimeStampDesc(remarksListRaw);
}

export function getDocuments(detail: any): any[] {
  const docs = detail?.ToDocument?.results || detail?.documents || [];
  return Array.isArray(docs) ? docs : [];
}

export function resolveDocType(value: any, docTypeMap: Record<string, string>): string {
  if (value == null || value === "") return "-";
  const v = String(value).trim();
  const LEGACY: Record<string, string> = { "0": "01", "1": "05" };
  const key = LEGACY[v] ?? v;
  return docTypeMap[key] || docTypeMap[v] || v;
}

export async function findAllMedicalAttendance(payload: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}): Promise<any[]> {
  const res: any = await Api.post(API_URLS.MEDICAL_ATTENDANCE.FIND_ALL, {
    page: payload.page ?? 1,
    limit: payload.limit ?? 100,
    ...(payload.status ? { status: payload.status } : {}),
    ...(payload.search ? { search: payload.search } : {}),
  });
  if (res?.success === false) {
    throw new Error(res?.message || "Failed to load requests");
  }
  return extractResults(res);
}

export async function findOneMedicalAttendance(id: string): Promise<any> {
  const res: any = await Api.post(API_URLS.MEDICAL_ATTENDANCE.FIND_ONE, { id });
  if (res?.success === false) {
    throw new Error(res?.message || "Failed to load details");
  }
  const root = unwrap(res);
  return root?.data?.data ?? root?.data ?? root;
}

export async function fetchApplicationTypes(): Promise<{ id: string; label: string }[]> {
  const res: any = await Api.post(API_URLS.MEDICAL_ATTENDANCE.APPLICATION_FOR, {});
  return extractDropdownOptions(res).filter((o) => o.id !== "CO");
}

export async function fetchHospitals(requestType: string): Promise<{ id: string; label: string }[]> {
  const res: any = await Api.post(API_URLS.MEDICAL_ATTENDANCE.HOSPITALS, { requestType });
  return extractDropdownOptions(res);
}

export async function fetchDiseases(requestType: string): Promise<{ id: string; label: string }[]> {
  const res: any = await Api.post(API_URLS.MEDICAL_ATTENDANCE.DISEASES, {
    requestType: requestType === "CO" ? "62" : requestType,
  });
  return extractDropdownOptions(res);
}

export async function fetchPatientNames(empId: string): Promise<any[]> {
  const res: any = await Api.post(API_URLS.MEDICAL_ATTENDANCE.PATIENT_NAMES, { empId });
  return extractResults(res);
}

export async function fetchRelationships(empId: string): Promise<any[]> {
  const res: any = await Api.post(API_URLS.MEDICAL_ATTENDANCE.RELATIONSHIPS, { empId });
  return extractResults(res);
}

export async function fetchDocumentTypes(): Promise<Record<string, string>> {
  const res: any = await Api.post(API_URLS.MEDICAL_ATTENDANCE.TYPE_OF_DOCUMENTS, {});
  const map: Record<string, string> = {};
  extractResults(res).forEach((item: any) => {
    const key = String(item.Value ?? "").trim();
    const label = String(item.Ddtext ?? "").trim();
    if (key) map[key] = label || key;
  });
  return map;
}

export async function checkIvfEligibility(empId: string): Promise<any> {
  const res: any = await Api.post(API_URLS.MEDICAL_ATTENDANCE.CHECK_IVF_ELIGIBILITY, { empId });
  return res;
}

export async function createOrUpdateMedicalAttendance(payload: any): Promise<any> {
  return Api.post(API_URLS.MEDICAL_ATTENDANCE.CREATE_OR_UPDATE, payload);
}

export async function approveRejectMedicalAttendance(payload: any): Promise<any> {
  return Api.post(API_URLS.MEDICAL_ATTENDANCE.APPROVE_REJECT, payload);
}

export function isMedicalAttendanceWorklistItem(task: {
  type?: string;
  taskText?: string;
  workItemText?: string;
}): boolean {
  const type = String(task?.type || "").toUpperCase();
  if (type === "MEDICAL_ATTENDANCE" || type === "MEDICAL") return true;
  const text = `${task?.taskText || ""} ${task?.workItemText || ""}`.toLowerCase();
  return (
    text.includes("ivf") ||
    text.includes("para 6.2") ||
    text.includes("para 6.3") ||
    text.includes("medical attendance")
  );
}

/** App shows workItemText for MEDICAL_ATTENDANCE (e.g. "IVF Request 6.2"), not the raw SAP type. */
export function getWorklistTypeLabel(task: {
  type?: string;
  workItemText?: string;
}): string {
  const type = String(task?.type || "").toUpperCase();
  if (type === "SAP_AUTHORIZATION_FORM") return "AUTHORIZATION FORM";
  if (type === "MEDICAL_ATTENDANCE" || type === "MEDICAL") {
    return String(task?.workItemText || "").trim() || "IVF / Para 6.2 / 6.3";
  }
  if (!type) return String(task?.workItemText || "").trim() || "General Task";
  return type.replace(/^NOC_/, "").replace(/_/g, " ");
}

export async function syncMedicalAttendance(empId: string): Promise<any> {
  const n = parseInt(String(empId).replace(/\D/g, ""), 10);
  return Api.post(API_URLS.MEDICAL_ATTENDANCE.SYNC, {
    empId: Number.isNaN(n) ? empId : String(n),
  });
}

export async function downloadMedicalAttendance(referenceNumber: string): Promise<string> {
  const res: any = await Api.post(API_URLS.MEDICAL_ATTENDANCE.DOWNLOAD, { referenceNumber });
  const root = unwrap(res);
  const url =
    root?.url ||
    root?.fileUrl ||
    root?.data?.url ||
    root?.data?.fileUrl ||
    res?.url;
  if (!url) throw new Error(res?.message || root?.message || "Download failed. Please try again.");
  return String(url);
}

export async function uploadMedicalAttendanceFile(file: File): Promise<{
  fileName: string;
  fileSize: string;
  fileExtension: string;
  serverKey: string;
  type: string;
}> {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (ext !== "pdf" && ext !== "jpg" && ext !== "jpeg") {
    throw new Error(`"${file.name}": Only PDF and JPG/JPEG files are supported.`);
  }
  const sizeMb = file.size / (1024 * 1024);
  if (ext === "pdf" && sizeMb > 2) {
    throw new Error(`"${file.name}": PDF size must not exceed 2 MB.`);
  }
  if ((ext === "jpg" || ext === "jpeg") && sizeMb > 5) {
    throw new Error(`"${file.name}": Image size must not exceed 5 MB.`);
  }
  const mimeType = ext === "pdf" ? "application/pdf" : "image/jpeg";
  const filestring = await fileToBase64(file);
  const res: any = await Api.post(API_URLS.AWS_BUCKET.UPLOAD_FILE, {
    filestring,
    mime_type: file.type && /^[a-z]+\//i.test(file.type) ? file.type : mimeType,
    module: "medical_attendance",
  });
  const data = unwrap(res);
  const key = data?.key || data?.data?.key;
  if (!key) {
    throw new Error(res?.message || data?.message || "Upload failed");
  }
  return {
    fileName: file.name || `document.${ext}`,
    fileSize: data.fileSize || `${(file.size / 1024).toFixed(2)} KB`,
    fileExtension: data.fileExtension || mimeType,
    serverKey: key,
    type: file.type && /^[a-z]+\//i.test(file.type) ? file.type : mimeType,
  };
}

export function parseLocalCardDate(d: string): Date | null {
  if (!d) return null;
  const parts = d.split("-").map(Number);
  if (parts.length !== 3) return null;
  if (parts[0] > 31) return new Date(parts[0], parts[1] - 1, parts[2]);
  return new Date(parts[2], parts[1] - 1, parts[0]);
}

export function mapApiItemToCard(
  apiItem: any,
  hospitalLabelMap?: Record<string, string>
) {
  const id = apiItem.id ?? apiItem.Id;
  const refNum =
    apiItem.ReferenceNumber && String(apiItem.ReferenceNumber).trim()
      ? apiItem.ReferenceNumber
      : "";
  const created = apiItem.RequestCreationDate
    ? String(apiItem.RequestCreationDate).trim()
    : apiItem.created_at
      ? formatSapDate(String(apiItem.created_at))
      : "";

  const requestTypeStr =
    apiItem.RequestTypeForMedicalAttendanceRules != null
      ? String(apiItem.RequestTypeForMedicalAttendanceRules)
      : "";

  let hospitalName = "-";
  if (requestTypeStr === "62") {
    hospitalName =
      (apiItem.HospitalName && String(apiItem.HospitalName).trim()) ||
      (apiItem.HospitalRecordKey != null ? String(apiItem.HospitalRecordKey) : "") ||
      "-";
  } else {
    const hospitalKey =
      apiItem.HospitalRecordKey != null ? String(apiItem.HospitalRecordKey) : "";
    hospitalName =
      (hospitalLabelMap && hospitalKey && hospitalLabelMap[hospitalKey]) ||
      hospitalKey ||
      "-";
  }

  return {
    id: String(id),
    referenceNumber: refNum,
    patientName: apiItem.PatientName ?? "-",
    hospitalName,
    requestCreationDate: created,
    requestStatus: apiItem.RequestStatus ?? "",
    requestType: requestTypeStr,
    raw: apiItem,
  };
}
